import { SIDEBAR_COLLAPSED_WIDTH } from '@backrs_ui/common-ui';

import { useResizableDivider } from '@backrs_ui/grow-ui/src/components/Lubav/hooks/useResizableDivider';
import {
  ResizableDivider,
  leftPanelWrapperSx,
  rightPanelWrapperSx,
} from '@backrs_ui/grow-ui/src/components/Lubav/ResizableDivider';
import { CONTENT_GUTTER } from '@backrs_ui/grow-ui/src/components/PageShell/pageShell.layout';
import { useAppSelector } from '@backrs_ui/grow-ui/src/redux/hooks';
import { getActiveLubavSession } from '@backrs_ui/grow-ui/src/redux/lubavSessionState/lubavSessionState.utils';

import { Box, SxProps, Theme, useMediaQuery } from '@mui/material';

import { ChatBox } from './ChatBox';
import { ResumeBuilderSolutionBox } from './ResumeBuilder/ResumeBuilderSolutionBox';
import { HEADER_HEIGHT } from '../Lubav/Header';

type SolutionSessionContentProps = Readonly<{
  mobileView: 'chat' | 'info';
  keyboardHeight: number;
}>;

export const SolutionSessionContent = ({
  mobileView,
  keyboardHeight,
}: SolutionSessionContentProps) => {
  const isMobile = useMediaQuery('(max-width: 900px)');
  const activeSession = useAppSelector(getActiveLubavSession);
  const { leftWidth, handleMouseDown } = useResizableDivider({
    containerId: 'solution-session-container',
    isMobile,
  });

  if (!activeSession) {
    return null;
  }

  if (isMobile) {
    return (
      <Box sx={mobileContainer(keyboardHeight)}>
        {mobileView === 'chat' ? (
          <ChatBox />
        ) : (
          <Box sx={mobileInfoBoxSx}>
            <ResumeBuilderSolutionBox />
          </Box>
        )}
      </Box>
    );
  }

  return (
    <Box id="solution-session-container" sx={container}>
      <Box sx={[leftPanelWrapperSx(leftWidth), leftPanelContainerSx] as SxProps<Theme>}>
        <ChatBox />
      </Box>
      <ResizableDivider onMouseDown={handleMouseDown} />
      <Box sx={[rightPanelWrapperSx(leftWidth), rightPanelContainerSx] as SxProps<Theme>}>
        <ResumeBuilderSolutionBox />
      </Box>
    </Box>
  );
};

const container: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'row',
  width: '100%',
  height: `calc(100vh - ${HEADER_HEIGHT}px)`,
  maxHeight: '100%',
  paddingLeft: { xs: 0, md: backrs.spacing['spacing-2xl'] },
});

const leftPanelContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  overflow: 'visible',
  boxSizing: 'border-box',
  paddingTop: 0,
  paddingBottom: backrs.spacing['spacing-xl'],
});

const rightPanelContainerSx: SxProps<Theme> = ({ backrs }) => ({
  height: '100%',
  boxSizing: 'border-box',
  paddingInline: backrs.spacing[CONTENT_GUTTER],
  paddingBottom: backrs.spacing['spacing-xl'],
});

const mobileContainer = (keyboardHeight: number): SxProps<Theme> => ({
  display: 'flex',
  flexDirection: 'column',
  width: '100vw',
  position: 'fixed',
  top: `${HEADER_HEIGHT}px`,
  '@media (max-width: 600px)': {
    top: `${HEADER_HEIGHT + 30}px`,
  },
  right: 0,
  bottom: `${keyboardHeight}px`,
  height: 'auto',
  overflow: 'hidden',
  overscrollBehavior: 'none',
  WebkitOverflowScrolling: 'touch',
});

const mobileInfoBoxSx: SxProps<Theme> = ({ backrs }) => ({
  overflowY: 'auto',
  height: '100%',
  flex: 1,
  paddingInline: backrs.spacing[CONTENT_GUTTER],
  ml: { xs: 0, sm: `${SIDEBAR_COLLAPSED_WIDTH}px`, md: 0 },
});
